import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { OrderContext } from '../../../../../context/OrderContext';
import { t } from '../../../../../types/translation/Translator';
import LoadingPackage from '../../../Common/LoadingPackage';
import OrderInfoPane from './OrderInfoPane';
import { testIds } from '../../../../../util/identifiers/identifiers.util';
import OrderDetailPane from './OrderDetailPane';
import { useMutation } from '@apollo/client';
import { UpdateOrderResponse, UpdateOrderVariables, OrderMutations } from '../../../../../graphql/order.graphql';
import ValidationModal from '../../../Common/ValidationModal';
import AddOrderProductsModal from '../Modals/AddOrderProductsModal';
import AssignOrderUserModal from '../Modals/AssignOrderUserModal';
import OrderUserPane from './OrderUserPane';
import Pane from '../../../../../VentoryUI/components/common/Pane';
import UpdateOrderPaneFooter from './Footer/UpdateOrderPaneFooter';
import { Order, OrderInput } from '../../../../../types/order/';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import { ProductTransaction } from '../../../../../types/productTransaction';
import { Routing } from '../../../../../util/routes.util';
import { useToast } from '../../../../../providers/ToastProvider';
import { ToastType } from '../../../../components/Toast';
import { CompanyContext } from '../../../../../context/CompanyContext';
import AddOrderCustomFieldModal from '../Modals/AddOrderCustomFieldModal';
import { System } from '../../../../../types/system';
import { useRequest } from '../../../../../requests/common/requestHook';
import { OrderRequest } from '../../../../../requests/order.request';
import IssueReturnModal from '../Modals/IssueReturnModal';

export default function UpdateOrderPane() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const id = useParams()['id'] || '';
  if (!id) return null; // TODO: Entity not found

  const { currentCompany } = useContext(CompanyContext);
  const { orders, setOrders, ordersLoading } = useContext(OrderContext);
  const { productTransactions } = useContext(ProductTransactionContext);

  const [orderInput, setOrderInput] = useState<OrderInput | undefined>();
  const [error, setError] = useState<string>('');

  const [openAddProductModal, setOpenAddProductModal] = useState<boolean>(false);
  const [openAssignOrderUserModal, setOpenAssignOrderUserModal] = useState<boolean>(false);
  const [openCompleteModal, setOpenCompleteModal] = useState<boolean>(false);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [openAddCustomFieldModal, setOpenAddCustomFieldModal] = useState<boolean>(false);
  const [openIssueReturnModal, setOpenIssueReturnModal] = useState<boolean>(false);

  useEffect(() => {
    const order = orders.get(id);
    if (order) setOrderInput(new OrderInput(order));
  }, [orders]);

  const navigateBack = () => {
    navigate(Routing.operations.orders.overview());
  };

  const [update, { loading: updateLoading }] = useMutation<UpdateOrderResponse, UpdateOrderVariables>(
    OrderMutations.update,
    {
      onCompleted: res => {
        const o = res.updateOrder[0];
        orders.set(o.id, new Order(o));
        setOrders(new Map(orders));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.orderUpdatedSuccessfullyToastTitle).singular.label,
        });
      },
    },
  );

  const handleUpdate = async () => {
    try {
      const input = orderInput?.forUpdate();
      if (!input) return;

      await update({
        variables: {
          orders: [input],
        },
      });
    } catch (e: any) {
      setError(String(e.message));
    }
  };

  const [cancel, { loading: cancelLoading }] = useRequest(OrderRequest.cancel);
  const handleCancel = async () => {
    if (!orderInput) return;

    const result = await cancel({ id: orderInput.id });
    if (!result) return;

    const order = result;
    orders.set(order.id, new Order(order));
    setOrders(new Map(orders));
    navigateBack();
  };

  const [complete, { loading: completeLoading }] = useRequest(OrderRequest.complete);
  const handleComplete = async () => {
    if (!orderInput) return;

    const result = await complete({ id: orderInput.id });
    if (!result) return;

    const order = result;
    orders.set(order.id, new Order(order));
    setOrders(new Map(orders));
    navigateBack();
  };

  // TODO_ORDER
  // const completeText =
  //   orderInput?.externalIdentifier?.settingsId === OrderUtil.SAP_IDENTIFIER && Order.hasPicking(orderInput)
  //     ? orderInput?.status === OrderStatus.pickingComplete
  //       ? t(TranslationKey.issueGoods).singular.label
  //       : t(TranslationKey.pickingComplete).singular.label
  //     : t(TranslationKey.completeOrder).singular.label;

  const completeText = t(TranslationKey.completeOrder).singular.label;

  // TODO_ORDER
  // const processCompletedOrders = currentCompany.settings.featureToggles.orders.processCompletedOrders;

  const footer = (canUpdate: boolean = false) => {
    if (!orderInput) return <></>;

    const hasOpenTransactions = [...productTransactions.values()].filter(
      (pt: ProductTransaction) =>
        pt.parentId === orderInput.id && pt.statusId === System.ProductTransactionStatus.createdId,
    ).length;

    return (
      <UpdateOrderPaneFooter
        order={orderInput}
        setOrder={setOrderInput}
        loading={{
          update: updateLoading,
        }}
        actions={{
          onManualCustomField: () => setOpenAddCustomFieldModal(true),
          onBack: () => navigateBack(),
          onAssigned: () => setOpenAssignOrderUserModal(true),
          onAddProduct: () => setOpenAddProductModal(true),
          onUpdate: () => handleUpdate(),
          onIssueReturn: () => setOpenIssueReturnModal(true),
        }}
      />
    );
  };

  const tabs = [
    {
      text: t(TranslationKey.order).singular.label,
      path: `detail`,
      key: 'detail',
    },
    {
      text: t(TranslationKey.user).plural.label,
      path: 'users',
      key: 'users',
    },
    {
      text: t(TranslationKey.info).singular.label,
      path: `info`,
      key: 'info',
    },
  ];

  useEffect(() => {
    if (orders.has(id)) setOrderInput(new OrderInput(orders.get(id)));
  }, [orders]);

  const content =
    (ordersLoading && !orders.get(id)) || !orderInput ? (
      <div className='grid h-full items-center justify-center'>
        <LoadingPackage />
      </div>
    ) : (
      <>
        <Routes>
          <Route
            path='detail'
            element={<OrderDetailPane order={orderInput} setOrder={setOrderInput} footer={footer} />}
          />
          <Route path='users' element={<OrderUserPane order={orderInput} setOrder={setOrderInput} footer={footer} />} />
          <Route path='info' element={<OrderInfoPane order={orderInput} setOrder={setOrderInput} footer={footer} />} />
          <Route path='/' element={<Navigate to='detail' replace={true} />} />
        </Routes>
        <AddOrderProductsModal open={openAddProductModal} setOpen={v => setOpenAddProductModal(v)} order={orderInput} />
        <AssignOrderUserModal
          open={openAssignOrderUserModal}
          setOpen={v => setOpenAssignOrderUserModal(v)}
          order={orderInput}
          setOrder={setOrderInput}
        />
        {openIssueReturnModal && (
          <IssueReturnModal open={openIssueReturnModal} setOpen={v => setOpenIssueReturnModal(v)} order={orderInput} />
        )}
        <ValidationModal
          open={openCompleteModal}
          setOpen={setOpenCompleteModal}
          onYes={() => handleComplete()}
          onNo={() => setOpenCompleteModal(false)}
          loading={completeLoading}
          title={completeText}
          content={t(TranslationKey.completeOrderValidation).singular.upper}
          subContent={'This will cancel all transactions that have not been processed'}
        />
        <ValidationModal
          open={openCancelModal}
          setOpen={setOpenCancelModal}
          onYes={() => handleCancel()}
          onNo={() => setOpenCancelModal(false)}
          loading={cancelLoading}
          title={t(TranslationKey.cancelOrder).singular.upper}
          content={t(TranslationKey.cancelOrderValidation).singular.upper}
          subContent={'This will cancel all transactions that have not been processed'}
        />
        {openAddCustomFieldModal && (
          <AddOrderCustomFieldModal
            open={openAddCustomFieldModal}
            setOpen={v => setOpenAddCustomFieldModal(v)}
            order={orderInput}
            setOrder={setOrderInput}
          />
        )}
      </>
    );

  return (
    <Pane tabs={tabs} testId={testIds.updateOrderPane} error={error}>
      {content}
    </Pane>
  );
}
